import React, { useEffect, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { LightText2 } from "@/common/ui/Text";
import SingleSelect from "@/common/custom/SingleSelect";
import SelectCustomDate from "@/common/custom/SelectCustomDate";
import Icon, { Image } from "@/common/ui/Image";
import Separator, { Center } from "@/common/css";
import { FiSearch } from "react-icons/fi";
import { activityIcon } from "@/dmr/common/SvgIcons";
import { useParams, useSearchParams } from "react-router-dom";
import Button from "@/common/ui/Button";
import { FaRegTrashCan } from "react-icons/fa6";
import { AiTwotoneEdit } from "react-icons/ai";
import { employeeSelector, setNoteEditor, setNoteUpdate } from "@/store/features/employeeSlice";
import { useAppDispatch } from "@/store/store";
import { useEmployeeInfo } from "@/services/employee.services";
import { ActivitiesSkeleton } from "@/common/ui/Loader";
import { useSelector } from "react-redux";
import EmptyList from "@/components/common/EmptyList";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import DeleteNote from "./modal/DeleteNote";
import { convertToLocalDateTime } from "@/utils/helper/moment";
import DOMPurify from "dompurify";
import "../../styles/editor-preview.css";

interface ActivitiesProps {
  tab: any;
  openModal: (modalKey: string, props?: any) => void;
  activeModal: string;
}

const options1 = [
  { label: "All activities", value: "activities" },
  { label: "Notes", value: "notes" },
];

const NOT_FOUND_MESSAGE: any = {
  "All activities": "No activities found yet",
  Notes: "You don’t have notes yet",
};

const options2 = [
  { label: "All time", value: "all-time" },
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "last7days" },
  { label: "Last 30 days", value: "last30days" },
  { label: "Custom period", value: "customPeriod" },
];
const modalConfig: ModalConfig = {
  DeleteNote: {
    component: DeleteNote,
    title: "Delete",
    type: "center",
    isQueryRequired: true,
  },
};

interface ActivityCardProps {
  user: { name: string; profilePic?: string };
  activity: string;
  date: string;
  dateTime: string;
  type?: string;
  openModal: any;
  noteId?: any;
  activityName?: string;
}

const getActivityIcon = (activityName?: string) => {
  if (activityName?.includes("_ATTACHMENT")) {
    return (
      <svg
        width="12"
        height="14"
        viewBox="0 0 12 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 !text-text-default"
      >
        <path
          d="M1.09082 0.5H7.97656L11.5 3.97754V12.9229C11.5 13.0738 11.4392 13.2204 11.3291 13.3291C11.2188 13.4379 11.0678 13.5 10.9092 13.5H1.09082C0.971773 13.5 0.857174 13.4648 0.760742 13.4014L0.670898 13.3291C0.560781 13.2204 0.5 13.0738 0.5 12.9229V1.07715C0.5 0.963828 0.533991 0.852875 0.597656 0.758789L0.670898 0.670898C0.753565 0.589383 0.859191 0.534359 0.973633 0.511719L1.09082 0.5Z"
          stroke="#595A61"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M8.72716 6.46146L6.54534 4.30762L4.36353 6.46146" stroke="#595A61" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.54541 4.30762L6.54541 10.2307" stroke="#595A61" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (activityName?.includes("_NOTE")) {
    return <Icon src="/icons/note-icon.svg" alt="Note" className="h-3.5 w-3 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 !text-text-default" />;
  }
  return activityIcon;
};

const ActivityCard: React.FC<ActivityCardProps> = ({ user, activity, activityName, dateTime, type, openModal, noteId }) => {
  const dispatch = useAppDispatch();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="flex gap-2 relative group">
      <div className="size-10 bg-text-gray3 rounded-full relative hidden md:block">
        {type === "note" ? (
          <Icon src="/icons/note-icon.svg" className="h-3.5 w-3 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 !text-text-default" alt="" />
        ) : (
          <>{getActivityIcon(activityName)}</>
        )}
      </div>
      <div className="bg-white shadow-sm rounded-lg p-2 md:p-4 mb-2 md:mb-4 flex space-x-3 w-full">
        {user?.profilePic ? (
          <img
            src={user?.profilePic}
            alt={"profile picture"}
            className="h-10 w-10 rounded-full object-cover object-center"
            style={{ minWidth: 40, minHeight: 40, maxWidth: 40, maxHeight: 40 }}
          />
        ) : (
          <div className="w-10 h-10 min-h-10 min-w-10 rounded-full bg-gray-300 flex justify-center items-center">
            <LightText2 text={user?.name ? user?.name[0] || "" + user?.name.split(" ")?.[1]?.[0] || "" : ""} />
          </div>
        )}
        <div className="flex flex-col flex-grow gap-y-3">
          <div className="flex justify-between">
            <LightText2 text={user?.name || ""} className="font-semibold truncate max-w-44" />
            <LightText2 text={convertToLocalDateTime(dateTime)} className="!text-10 md:!text-13" />
          </div>
          <div className="relative">
            <div
              className={`editor-preview text-13 rich-text-content text-text-default overflow-hidden text-ellipsis ${!isExpanded ? "line-clamp-4" : ""}`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activity) }}
            />
            {activity.length > 200 && (
              <button onClick={handleToggle} className="text-text-primary font-medium text-sm">
                {isExpanded ? "Show Less" : "Show More"}
              </button>
            )}
          </div>
        </div>
      </div>
      {type === "note" && (
        <div className="absolute top-10 right-2 group-hover:flex items-center justify-center hidden gap-1">
          <button
            className="size-9 border-[0.5px] hover:border border-borderColor1 bg-[#E6F2FE]  flex items-center rounded hover:z-20 hover:rounded hover:border-text-primary hover:bottom-4 active:ring active:ring-brand-shade active:ring-offset-1 justify-center"
            onClick={() => dispatch(setNoteUpdate({ noteData: activity, noteId }))}
          >
            <AiTwotoneEdit className="text-text-primary size-4" />
          </button>
          <button
            className="size-9 border-[0.5px] hover:border flex border-borderColor1 bg-[#E6F2FE] items-center rounded hover:rounded hover:border-text-primary hover:bottom-2 active:ring active:ring-brand-shade active:ring-offset-1 justify-center"
            onClick={() => openModal("DeleteNote", { data: { noteId } })}
          >
            <FaRegTrashCan className="text-text-error size-4" />
          </button>
        </div>
      )}
    </div>
  );
};

interface SearchBarProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  query?: string;
  className?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({ query = "", onSearch, placeholder = "Search...", className = "" }) => {
  const [searchInput, setSearchInput] = useState(query);

  const handleSearch = () => {
    onSearch(searchInput.trim());
  };

  const handleClear = () => {
    setSearchInput("");
    onSearch("");
  };

  return (
    <div className="relative md:px-2 ">
      <input
        type="text"
        placeholder={placeholder}
        value={searchInput}
        onChange={(e) => {
          let value = e.target.value;
          if (value?.trim().length === 0) {
            value = value.trim();
          }
          setSearchInput(value);
        }}
        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        className={`pl-2 pr-14 py-1.5 rounded-md border border-gray-300 text-13 !leading-normal outline-none focus:ring-1 focus:ring-text-primary ${className}`}
      />
      <Center className="absolute right-3 top-1/2 -translate-y-1/2">
        {searchInput.length > 0 ? (
          <Center className="gap-1">
            <RxCross2 className="size-5 text-text-default hover:cursor-pointer" onClick={handleClear} />
            <Image src="/icons/search.svg" alt="" className="size-5 hover:cursor-pointer" onClick={handleSearch} />
          </Center>
        ) : (
          <FiSearch className="text-text-primary" />
        )}
      </Center>
    </div>
  );
};

const headerTabs = [
  { value: "activities", label: "All activities" },
  { value: "notes", label: "Notes" },
];
interface ActivityHeaderProps {
  currentTab: any;
  onTabChange: (tab: any) => void;
  searchQuery: string;
  onSearch: (query: string) => void;
  filter: { type: any; time: any };
  onFilterChange: (filter: { type: any; time: any }) => void;
  notesCount?: number;
  totalCount?: number;
  onAddNote?: () => void;
}
const ActivityHeader: React.FC<ActivityHeaderProps> = ({ currentTab, onTabChange, onSearch, filter, onFilterChange, notesCount, totalCount, onAddNote }) => {
  return (
    <div>
      <Center className=" justify-between pr-4">
        <div className="flex p-4 gap-x-2">
          {headerTabs.map((tab) => (
            <div
              key={tab.value}
              className={`cursor-pointer text-center text-13  ${
                currentTab.value === tab.value ? "border-b-2 border-text-primary font-bold text-text-primary" : "text-text-default"
              }`}
              onClick={() => onTabChange(tab)}
            >
              {tab.label + ` (${tab.value === "notes" ? notesCount ?? 0 : totalCount ?? 0})`}
            </div>
          ))}
        </div>
        {currentTab.value === "notes" && onAddNote && (
          <Button
            variant="outline"
            size="small"
            onClick={onAddNote}
            className=" border-text-primary border font-medium rounded-50 bg-primaryOpacity-5 text-text-primary"
            leftIcon={
              <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9 14.2083H1.5C1.23478 14.2083 0.98043 14.1029 0.792893 13.9154C0.605357 13.7278 0.5 13.4735 0.5 13.2083V2.20825C0.5 1.94304 0.605357 1.68868 0.792893 1.50115C0.98043 1.31361 1.23478 1.20825 1.5 1.20825H12.5C12.7652 1.20825 13.0196 1.31361 13.2071 1.50115C13.3946 1.68868 13.5 1.94304 13.5 2.20825V9.70825L9 14.2083Z"
                  stroke="#6F36BC"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
                <path
                  d="M9 10.2083V14.2083L13.5 9.70825H9.5C9.36739 9.70825 9.24021 9.76093 9.14645 9.8547C9.05268 9.94847 9 10.0756 9 10.2083Z"
                  stroke="#6F36BC"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            }
          >
            Add notes
          </Button>
        )}
      </Center>
      <div className="w-full flex items-center gap-x-2 mb-4">
        {currentTab.value === "activities" && (
          <div className="flex items-center px-3 h-full flex-wrap md:flex-nowrap w-full">
            <LightText2 text="Filter by" className="text-13 pr-2" />
            <SingleSelect
              options={options1}
              placeholder="Select activity type"
              onChange={(value) => onFilterChange({ ...filter, type: value })}
              selectedValue={filter.type}
              containerClass="flex item-center w-fit pr-2"
              textClassname="text-13"
              className="w-full text-13 leading-normal font-normal text-text-primary gap-1 !p-0"
              dropdownClassName="w-[120px] top-8 z-[60]"
              listClassName="max-h-[300px] overflow-y-auto text-text-default bg-white "
              activeClass="text-text-primary"
            />
            <Separator isVertical className=" bg-fieldBorder h-1/2" />
            <SelectCustomDate
              options={options2}
              placeholder="Choose a date range"
              onChange={(value) => {
                onFilterChange({ ...filter, time: value });
              }}
              selectedValue={filter.time}
            />
            <Separator isVertical className=" bg-fieldBorder h-1/2" />
            <SearchBar onSearch={onSearch} className="w-full md:w-auto" />
          </div>
        )}
      </div>
    </div>
  );
};
interface ActivityContentProps {
  isLoading: boolean;
  data: any;
  currentTab: any;
  openModal: any;
}
const ActivityContent: React.FC<ActivityContentProps> = ({ currentTab, isLoading, data, openModal }) => {
  if (isLoading) return <ActivitiesSkeleton />;
  if (!data || !Object.keys(data.groupedActivities).length)
    return (
      <EmptyList
        text={NOT_FOUND_MESSAGE[currentTab.label]}
        icon="/icons/document.svg"
        className="h-full gap-y-4 mt-16"
        textClassName="!font-semibold"
      ></EmptyList>
    );

  return (
    <div className="overflow-y-auto h-[calc(100%-100px)]">
      <div className="space-y-2 md:space-y-4 border border-stroke-light bg-footerBg w-full p-2 md:p-4">
        {Object.entries(data.groupedActivities).map(([date, activities]: any) => (
          <div key={date} className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-600 mb-1">{date}</h4>
            {activities.map((activity: any, index: number) => (
              <ActivityCard key={`${date}-${index}`} {...activity} date={date} openModal={openModal} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const ActivitiesM: React.FC<ActivitiesProps> = ({ tab, openModal, activeModal }) => {
  const { employeeId } = useParams();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { view } = useSelector(employeeSelector);
  const [currentHeaderTab, setCurrentHeaderTab] = useState(headerTabs[0]);
  const [filter, setFilter] = useState({ type: options1[0], time: options2[0] });
  const { isLoading, data, refetch } = useEmployeeInfo({
    empId: employeeId,
    view_typ: tab.apiKey,
    query: searchQuery,
    type: currentHeaderTab.value,
    ...(currentHeaderTab.value === "activities" ? { filter } : {}),
  });
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const headerTab = searchParams.get("tab");

  useEffect(() => {
    if (headerTab) {
      const isValidTab = headerTabs.find((h) => h.value === headerTab);
      if (isValidTab) {
        setCurrentHeaderTab(isValidTab);
      }
    }
  }, [headerTab]);

  useEffect(() => {
    if (view.refresh === "activity") {
      refetch();
    }
  }, [view]);

  useEffect(() => {
    if (view.refresh === "activity") {
      refetch();
    }
  }, [view]);

  useEffect(() => {
    if (!activeModal) {
      setTimeout(() => {
        refetch();
      }, 100);
    }
  }, [activeModal]);

  useEffect(() => {
    if (currentHeaderTab) {
      setFilter({ type: options1[0], time: options2[0] });
    }
  }, [currentHeaderTab.value]);

  return (
    <div className="flex flex-col w-full h-full">
      <ActivityHeader
        currentTab={currentHeaderTab}
        onTabChange={setCurrentHeaderTab}
        searchQuery={searchQuery}
        onSearch={(query) => {
          setSearchQuery(query);
          refetch();
        }}
        filter={filter}
        onFilterChange={setFilter}
        notesCount={data?.notesCount}
        totalCount={data?.total}
        onAddNote={currentHeaderTab.value === "notes" ? () => dispatch(setNoteEditor(true)) : undefined}
      />
      <ActivityContent isLoading={isLoading} data={data} currentTab={currentHeaderTab} openModal={openModal} />
    </div>
  );
};

export const Activities = withModal(ActivitiesM, modalConfig);
